import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type CSSProperties,
  type ClipboardEvent,
  type KeyboardEvent,
} from 'react';
import { Box } from '@chakra-ui/react';
import { MentionSuggestionsPopup } from './MentionSuggestionsPopup';
import { plainTextFromDom, serializeDom } from './mentionUtils';
import { focusEnd, hydrateRoot, moveCaretAfter } from './mentionDom';
import { mentionEditorSx } from './MentionEditor.styles';
import { useMentionSuggestions } from './useMentionSuggestions';
import type {
  MentionEditorHandle,
  MentionEditorProps,
} from './MentionEditor.types';

export type { MentionEditorHandle, MentionEditorProps };

export const MentionEditor = forwardRef<
  MentionEditorHandle,
  MentionEditorProps
>(
  (
    {
      placeholder,
      multiline = false,
      autoFocus = false,
      initialValue,
      onChange,
      onKeyDown,
      onKeyUp,
      className,
      minHeight,
      maxHeight,
      testId,
    },
    ref
  ) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const [isEmpty, setIsEmpty] = useState(true);

    // Recompute the "is the editor visually empty" state so the CSS
    // placeholder pseudo-element can be hidden when the user has typed.
    const refreshEmpty = useCallback(() => {
      const el = rootRef.current;
      if (!el) return;
      const text = plainTextFromDom(el);
      setIsEmpty(text.length === 0);
    }, []);

    const onInserted = useCallback(
      (plainText: string) => {
        refreshEmpty();
        onChange?.(plainText);
      },
      [onChange, refreshEmpty]
    );

    const {
      query,
      suggestions,
      loading,
      highlightedIndex,
      setHighlightedIndex,
      popupPosition,
      closePopup,
      detectTrigger,
      insertMention,
    } = useMentionSuggestions({ rootRef, onInserted });

    useImperativeHandle(
      ref,
      (): MentionEditorHandle => ({
        getSerialized: () =>
          rootRef.current ? serializeDom(rootRef.current) : '',
        getPlainText: () =>
          rootRef.current ? plainTextFromDom(rootRef.current) : '',
        clear: () => {
          if (rootRef.current) {
            rootRef.current.innerHTML = '';
            setIsEmpty(true);
            onChange?.('');
          }
          closePopup();
        },
        focus: () => {
          if (rootRef.current) focusEnd(rootRef.current);
        },
        setText: (text: string) => {
          const el = rootRef.current;
          if (!el) return;
          el.textContent = text;
          setIsEmpty(text.length === 0);
          onChange?.(text);
          focusEnd(el);
          closePopup();
        },
      }),
      [closePopup, onChange]
    );

    // One-time hydration from initialValue. Subsequent prop changes are
    // ignored — once the user has begun editing, the DOM is the source of
    // truth and we don't want to clobber it from above.
    useEffect(() => {
      const el = rootRef.current;
      if (!el) return;
      if (initialValue && initialValue.length > 0) {
        hydrateRoot(el, initialValue);
      }
      refreshEmpty();
      if (autoFocus) focusEnd(el);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleInput = useCallback(() => {
      const root = rootRef.current;
      if (!root) return;
      onChange?.(plainTextFromDom(root));
      refreshEmpty();
      detectTrigger();
    }, [detectTrigger, onChange, refreshEmpty]);

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLDivElement>) => {
        // Popup steals navigation / commit keys.
        if (query !== null && suggestions.length > 0) {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setHighlightedIndex((i) => (i + 1) % suggestions.length);
            return;
          }
          if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlightedIndex(
              (i) => (i - 1 + suggestions.length) % suggestions.length
            );
            return;
          }
          if (event.key === 'Enter' || event.key === 'Tab') {
            event.preventDefault();
            const picked = suggestions[highlightedIndex];
            if (picked) insertMention(picked);
            return;
          }
          if (event.key === 'Escape') {
            event.preventDefault();
            closePopup();
            return;
          }
        }
        // Empty popup state — Escape still closes so the user can dismiss.
        if (query !== null && event.key === 'Escape') {
          event.preventDefault();
          closePopup();
          return;
        }

        onKeyDown?.(event);
        if (event.defaultPrevented) return;

        // Single-line mode never inserts a literal newline.
        if (!multiline && event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          return;
        }
        // Multiline mode: native Enter on contenteditable defaults to a
        // new <div> wrapper which complicates serialization. Force a
        // simple <br> instead.
        if (multiline && event.key === 'Enter' && event.shiftKey) {
          event.preventDefault();
          const sel = window.getSelection();
          if (!sel || sel.rangeCount === 0) return;
          const range = sel.getRangeAt(0);
          range.deleteContents();
          const br = document.createElement('br');
          range.insertNode(br);
          // Zero-width text node so the caret has somewhere to land after
          // the <br> when it's the last child.
          const placeholder = document.createTextNode('​');
          br.after(placeholder);
          moveCaretAfter(placeholder);
          handleInput();
        }
      },
      [
        closePopup,
        handleInput,
        highlightedIndex,
        insertMention,
        multiline,
        onKeyDown,
        query,
        setHighlightedIndex,
        suggestions,
      ]
    );

    const handleKeyUp = useCallback(
      (event: KeyboardEvent<HTMLDivElement>) => {
        onKeyUp?.(event);
        // Arrow keys / mouse clicks move the caret without firing input,
        // so refresh trigger detection here too.
        if (
          event.key === 'ArrowLeft' ||
          event.key === 'ArrowRight' ||
          event.key === 'Home' ||
          event.key === 'End'
        ) {
          detectTrigger();
        }
      },
      [detectTrigger, onKeyUp]
    );

    const handlePaste = useCallback(
      (event: ClipboardEvent<HTMLDivElement>) => {
        event.preventDefault();
        const text = event.clipboardData?.getData('text/plain') ?? '';
        const cleaned = multiline ? text : text.replace(/\r?\n/g, ' ');
        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;
        const range = sel.getRangeAt(0);
        range.deleteContents();
        const frag = document.createDocumentFragment();
        const lines = cleaned.split('\n');
        lines.forEach((line, i) => {
          if (i > 0) frag.appendChild(document.createElement('br'));
          if (line) frag.appendChild(document.createTextNode(line));
        });
        const lastNode = frag.lastChild;
        range.insertNode(frag);
        if (lastNode) moveCaretAfter(lastNode);
        handleInput();
      },
      [handleInput, multiline]
    );

    const handleBlur = useCallback(() => {
      // Close on blur but with a small delay so a click on the popup can
      // resolve first (popup's onMouseDown preventDefault handles the
      // ordering, but blur fires anyway on click-outside).
      setTimeout(() => {
        if (document.activeElement === rootRef.current) return;
        closePopup();
      }, 120);
    }, [closePopup]);

    const rootStyle: CSSProperties = {
      minHeight: minHeight ? `${minHeight}px` : undefined,
      maxHeight: maxHeight ? `${maxHeight}px` : undefined,
      overflowY: maxHeight ? 'auto' : undefined,
      outline: 'none',
      whiteSpace: multiline ? 'pre-wrap' : 'nowrap',
      wordBreak: multiline ? 'break-word' : 'normal',
      overflowX: multiline ? 'hidden' : 'auto',
    };

    return (
      <Box position="relative" width="100%">
        <Box
          ref={rootRef}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline={multiline}
          aria-label={placeholder}
          data-placeholder={placeholder}
          data-multiline={multiline}
          data-test-id={testId ?? 'mention-editor'}
          data-empty={isEmpty}
          className={className}
          style={rootStyle}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          onPaste={handlePaste}
          onBlur={handleBlur}
          onMouseUp={() => detectTrigger()}
          sx={mentionEditorSx}
          pl="5px"
        />
        <MentionSuggestionsPopup
          open={query !== null}
          loading={loading}
          query={query ?? ''}
          suggestions={suggestions}
          highlightedIndex={highlightedIndex}
          position={popupPosition}
          onSelect={insertMention}
          onHover={setHighlightedIndex}
        />
      </Box>
    );
  }
);

MentionEditor.displayName = 'MentionEditor';
